import {JSX, useCallback, useEffect, useState} from 'react';
import { useField } from "formik";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {concepts, CustomField, Institute, SubscriptionRule} from "fearless-common";
import { Lock, Person } from "react-bootstrap-icons";
import { Alert } from 'fearless-common-ui';
import FloatingLabelField from "../../components/forms/FloatingLabelField";
import useCheckExistingUser from "./useCheckExistingUser";
import useGet from "../../components/hooks/useGet";
import { backendEndpoints } from "../../libs/backendEndpoints";
import { validateEmail } from "../../utils/validations";
import avansLogo from "fearless-common-ui/public/assets/contributors/institutes-logos/avans.png";
import lumcLogo from "fearless-common-ui/public/assets/contributors/institutes-logos/lumc-logo.jpg";
import hogeschoolLeidenLogo from "fearless-common-ui/public/assets/contributors/institutes-logos/hs-leiden.png";
import Loading from "../../components/Loading";

/** Root component; individual field groups are exposed as static properties. */
export default function SignUpFields(): JSX.Element {
    return (
        <>
        </>
    );
}

const usernameFieldName = 'username';
const emailFieldName = 'email';
const organisationName = 'organisation';
const organisationSuggestionName = 'organisation_suggestion';

/** Fields for the user's account details: name, email, organization, group name, and position. */
function Account({ showEmail = false }: {
    /** Whether to show the email field (default: false). */
    showEmail?: boolean;
}) {
    const [emailField] = useField(emailFieldName);
    const [organisationField,, { setValue: setOrganisation }] = useField(organisationName);
    const [{ value: organisationSuggestionValue }] = useField(organisationSuggestionName);
    const { alert } = useCheckExistingUser({ query: emailField.value, checkDuplicate: true });

    return (
        <>
            <Row>
                <Col>
                    <FloatingLabelField name="first_name" label="First name" required />
                </Col>
                <Col>
                    <FloatingLabelField name="last_name" label="Last name" required />
                </Col>
            </Row>
            {showEmail && <FloatingLabelField name={emailFieldName} label="E-mail" required />}
            {alert}
            <FloatingLabelField
              name={organisationName}
              label="Organization"
              helperText={typeof organisationField.value === 'string'
                && typeof organisationSuggestionValue === 'string'
                && organisationSuggestionValue.toLowerCase().includes(organisationField.value.toLowerCase())
                && organisationField.value.toLowerCase() !== organisationSuggestionValue.toLowerCase()
                && (
                  <p className="ps-4 d-block text-start">
                      Do you mean&nbsp;
                      <button className="text-link p-0 text-decoration-underline text-primary bg-transparent border-0" onClick={() => setOrganisation(organisationSuggestionValue)}>
                          {organisationSuggestionValue}
                      </button>
                      ?
                  </p>
                )}
              required
            />
            <FloatingLabelField name="group_name" label={concepts.GroupName} />
            <FloatingLabelField name="position" label="Position" />
        </>
    );
}
SignUpFields.Account = Account;

/** Initial Formik values for the sign-up form field groups. */
export const signUpInitialValues = {
    Account: {
        first_name: '',
        last_name: '',
        organisation: '',
        group_name: '',
        position: '',
    },
    UsernamePassword: {
        username: '',
        password: '',
        password_copy: '',
    },
};

/** Fields for choosing a username and setting a password. */
const UserNamePassword = ({ checkDuplicate }: {
    /** Whether to check the username for duplicates as the user types. */
    checkDuplicate?: boolean;
}) => {
    const [usernameField] = useField(usernameFieldName);
    const { alert } = useCheckExistingUser({ query: usernameField.value, checkDuplicate });

    return (
        <>
            <FloatingLabelField
                name={usernameFieldName}
                label={concepts.Username}
                required
                icon={<Person />}
                autoFocus
            />
            {alert}
            <FloatingLabelField name="password" label="Password" type="password" icon={<Lock />} required />
            <FloatingLabelField name="password_copy" label="Confirm password" type="password" icon={<Lock />} required />
        </>
    );
};
SignUpFields.UsernamePassword = UserNamePassword;

const customFieldsName = 'custom_fields';

const instituteNameToLogo: Record<string, string> = {
    'avans hogeschool': avansLogo,
    'avans': avansLogo,
    'lumc': lumcLogo,
    'hogeschool leiden': hogeschoolLeidenLogo,
    'hs leiden': hogeschoolLeidenLogo,
};

type ResponseData = SubscriptionRule & { institute?: Institute; signup_custom_fields: Record<string, CustomField> }

/**
 * In the backend, we can configure extra fields when signing up via the 'signup_custom_fields'
 * attribute of the SubscriptionRule model. This component renders those custom fields based
 * on the subscription rule matched to the user's email domain.
 */
function InstituteFields() {
    const [{ value: customFields },, { setValue: setCustomFields }] = useField<Record<string, string>>(customFieldsName);
    const [emailField] = useField(emailFieldName);
    const [, , { setValue: setOrganisationNameSuggestion }] = useField(organisationSuggestionName);
    const onSuccess = useCallback((newRule: ResponseData) => {
        setOrganisationNameSuggestion(newRule.institute?.name || '');
        if (newRule?.signup_custom_fields) {
            setCustomFields(
                // Only keep fields that are required for this subscription rule
                Object.keys(newRule?.signup_custom_fields).reduce<Record<string, string>>((acc, key) => {
                    acc[key] = customFields[key] || '';
                    return acc;
                }, {})
            );
        } else {
            setCustomFields({});
        }
    }, [customFields, setCustomFields]);
    const { data: rule } = useGet<ResponseData>({
        endpoint: validateEmail(emailField.value) ? backendEndpoints.matchSubscriptionRule : '',
        params: { email: emailField.value },
        smoothRefresh: true,
        retry: 1,
        onSuccess,
    });

    const lowercase = rule?.institute?.name?.toLowerCase();
    const logo = lowercase && lowercase in instituteNameToLogo ? instituteNameToLogo[lowercase] : undefined;

    const signUpFields: Record<string, string> | undefined = rule?.signup_custom_fields;

    useEffect(() => {

    }, [signUpFields]);

    return signUpFields && typeof signUpFields === 'object' && !Array.isArray(signUpFields) && Object.keys(signUpFields).length > 0 ? (
        <div className="mt-5">
            {logo && <img src={logo} height={50} className="mb-3" alt="Logo" />}
            <p>
                <strong>Looks like you're joining Fearless League through {rule?.institute?.name}.</strong>
            </p>
            {Object.keys(signUpFields).map((key) => (
                <FloatingLabelField
                    key={key}
                    name={`custom_fields[${key}]`}
                    label={signUpFields[key]}
                    required
                />
            ))}
        </div>
    ) : null;
}
SignUpFields.InstituteFields = InstituteFields;

/** Formik field name for the sign-up promo code field. */
export const signUpCodeName = 'sign_up_code';

interface PromoCodeData {
    enticing_message?: string;
    number_of_months?: number;
}

/** Field for entering and validating a promotional sign-up code. */
function SignUpCode() {
    const [{ value }] = useField<string>(signUpCodeName);
    const [searchValue, setSearchValue] = useState(value);
    const { isLoading, data } = useGet<boolean>({
        endpoint: searchValue ? backendEndpoints.checkAdminSignUpCode(searchValue) : '',
    });
    return (
        <>
            <FloatingLabelField
                name={signUpCodeName}
                label="Promo code"
                onKeyUp={(e) => {
                    setTimeout(() => {
                        setSearchValue(e.target.value);
                    }, 300);
                }}
            />
            {isLoading && <span className="d-flex align-items-center gap-2 animated fadeIn"><Loading size="sm" /><span>Validating code...</span></span>}
            {!isLoading && value && data === false && <p className="text-danger animated fadeIn">This code is not valid</p>}
            {!isLoading && value && typeof data === 'object' && data !== null && (
                <Alert variant="success" className="d-flex align-items-center rounded-4 animated">
                    <span>
                        <b>{(data as PromoCodeData).enticing_message}</b>
                        {(data as PromoCodeData).enticing_message && <br />}
                        You'll have {(data as PromoCodeData).number_of_months} months free!
                    </span>
                </Alert>
            )}
        </>
    );
}
SignUpFields.SignUpCode = SignUpCode;
